<template>
  <!-- PWA Install Modal -->
  <div v-if="showBanner" class="modal-overlay" @click="dismissBanner">
    <div class="pwa-install-modal" :class="{ ios: isIOS }" @click.stop>
      <button
        class="modal-close"
        aria-label="Close modal"
        @click="dismissBanner"
      >
        <span class="close-icon">&times;</span>
      </button>

      <div class="modal-header">
        <div class="app-icon">
          <img src="/favicon.svg" alt="Candy Cleaning Services" class="icon" />
        </div>
        <h2 class="modal-title">Install Candy Cleaning Services</h2>
        <p class="modal-subtitle">
          {{
            isIOS
              ? 'Add to your home screen for quick access'
              : 'Get the full app experience with faster loading and offline access'
          }}
        </p>
      </div>

      <div class="modal-content">
        <div class="benefits-list">
          <div class="benefit-item">
            <span class="benefit-icon">⚡</span>
            <span class="benefit-text">Faster loading times</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-icon">📱</span>
            <span class="benefit-text">Native app experience</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-icon">🔄</span>
            <span class="benefit-text">Works offline</span>
          </div>
          <div class="benefit-item">
            <span class="benefit-icon">🔔</span>
            <span class="benefit-text">Push notifications</span>
          </div>
        </div>
      </div>

      <div class="modal-actions">
        <button
          v-if="!isIOS && isInstallable"
          class="install-btn primary"
          :disabled="installing"
          @click="handleInstall"
        >
          {{ installing ? 'Installing...' : 'Install App' }}
        </button>

        <button
          v-if="isIOS"
          class="install-btn primary"
          @click="showIOSInstructions = true"
        >
          Show Install Instructions
        </button>

        <button class="install-btn secondary" @click="dismissBanner">
          Maybe Later
        </button>
      </div>
    </div>
  </div>

  <!-- iOS Installation Instructions Modal -->
  <div
    v-if="showIOSInstructions"
    class="modal-overlay"
    @click="showIOSInstructions = false"
  >
    <div class="ios-instructions-modal" @click.stop>
      <button class="modal-close" @click="showIOSInstructions = false">
        &times;
      </button>
      <h3>Install Candy Cleaning App</h3>
      <div class="instructions">
        <div class="instruction-step">
          <span class="step-number">1</span>
          <div class="step-content">
            <p>
              Tap the <strong>Share</strong> button
              <span class="share-icon">📤</span> in your browser toolbar
            </p>
          </div>
        </div>
        <div class="instruction-step">
          <span class="step-number">2</span>
          <div class="step-content">
            <p>
              Scroll down and tap <strong>"Add to Home Screen"</strong>
              <span class="home-icon">📱</span>
            </p>
          </div>
        </div>
        <div class="instruction-step">
          <span class="step-number">3</span>
          <div class="step-content">
            <p>
              Tap <strong>"Add"</strong> to install the app on your home screen
            </p>
          </div>
        </div>
      </div>
      <div class="instruction-actions">
        <button class="modal-btn" @click="showIOSInstructions = false">
          Got it!
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { usePWA } from '@/composables/usePWA'
import { createLogger } from '@/utils/logger'

const logger = createLogger('PWAInstallBanner')

const { isInstalled, isInstallable, isIOS, installPWA, canInstall } = usePWA()

const installing = ref(false)
const dismissed = ref(false)
const showIOSInstructions = ref(false)

// Check if banner was previously dismissed (store in localStorage)
const BANNER_DISMISSED_KEY = 'pwa-banner-dismissed'
const BANNER_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000 // 7 days

const showBanner = computed(() => {
  return canInstall() && !dismissed.value && !isInstalled.value
})

const handleInstall = async () => {
  installing.value = true
  try {
    const success = await installPWA()
    if (success) {
      dismissed.value = true
    }
  } catch (error) {
    logger.error('Failed to install PWA:', error)
  } finally {
    installing.value = false
  }
}

const dismissBanner = () => {
  dismissed.value = true
  // Store dismissal timestamp
  localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString())
}

const checkDismissalStatus = () => {
  const dismissedTime = localStorage.getItem(BANNER_DISMISSED_KEY)
  if (dismissedTime) {
    const timeSinceDismissal = Date.now() - parseInt(dismissedTime)
    if (timeSinceDismissal < BANNER_DISMISS_DURATION) {
      dismissed.value = true
    } else {
      // Remove expired dismissal
      localStorage.removeItem(BANNER_DISMISSED_KEY)
    }
  }
}

onMounted(() => {
  checkDismissalStatus()
})
</script>

<style scoped>
/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* PWA Install Modal */
.pwa-install-modal {
  background: white;
  border-radius: 16px;
  max-width: 480px;
  width: 100%;
  position: relative;
  color: #333;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  cursor: pointer;
  color: #666;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  z-index: 1;
}

.modal-close:hover {
  background: rgba(0, 0, 0, 0.1);
  transform: scale(1.05);
}

.close-icon {
  font-size: 1.5rem;
  font-weight: 300;
}

/* Modal Header */
.modal-header {
  text-align: center;
  padding: 2rem 2rem 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;
}

.app-icon {
  margin-bottom: 1rem;
}

.icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.15);
  padding: 12px;
  filter: brightness(0) invert(1);
}

.modal-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
}

.modal-subtitle {
  margin: 0;
  font-size: 1rem;
  opacity: 0.9;
  line-height: 1.5;
}

/* Modal Content */
.modal-content {
  padding: 1.5rem 2rem;
}

.benefits-list {
  display: grid;
  gap: 1rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.benefit-item:hover {
  background: #f1f5f9;
  transform: translateY(-1px);
}

.benefit-icon {
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.benefit-text {
  font-weight: 500;
  color: #374151;
  font-size: 0.95rem;
}

/* Modal Actions */
.modal-actions {
  padding: 0 2rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.install-btn {
  border: none;
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  position: relative;
  overflow: hidden;
}

.install-btn.primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.install-btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.install-btn.primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.install-btn.secondary {
  background: transparent;
  color: #6b7280;
  border: 2px solid #e5e7eb;
}

.install-btn.secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
  color: #374151;
}

/* iOS Instructions Modal */
.ios-instructions-modal {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 440px;
  width: 100%;
  position: relative;
  color: #333;
  animation: slideUp 0.3s ease-out;
}

.ios-instructions-modal .modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.ios-instructions-modal .modal-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.ios-instructions-modal h3 {
  margin: 0 0 1.5rem 0;
  color: #1f2937;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
}

.instructions {
  margin-bottom: 2rem;
}

.instruction-step {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #667eea;
}

.step-number {
  background: #667eea;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 2px;
}

.step-content p {
  margin: 0;
  line-height: 1.6;
  color: #374151;
  font-size: 0.95rem;
}

.share-icon,
.home-icon {
  font-size: 1.125rem;
  margin: 0 2px;
}

.instruction-actions {
  display: flex;
  justify-content: center;
}

.modal-btn {
  background: #667eea;
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
}

.modal-btn:hover {
  background: #5a67d8;
  transform: translateY(-1px);
}

/* iOS Specific Styling */
.pwa-install-modal.ios .modal-header {
  background: linear-gradient(135deg, #007aff 0%, #5856d6 100%);
}

.pwa-install-modal.ios .install-btn.primary {
  background: linear-gradient(135deg, #007aff 0%, #5856d6 100%);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.pwa-install-modal.ios .install-btn.primary:hover:not(:disabled) {
  box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .pwa-install-modal {
    max-width: 100%;
    margin: 0.5rem;
  }

  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }

  .icon {
    width: 56px;
    height: 56px;
  }

  .modal-title {
    font-size: 1.25rem;
  }

  .modal-subtitle {
    font-size: 0.9rem;
  }

  .modal-content {
    padding: 1rem 1.5rem;
  }

  .modal-actions {
    padding: 0 1.5rem 1.5rem;
  }

  .ios-instructions-modal {
    margin: 0.5rem;
    padding: 1.5rem;
  }

  .instruction-step {
    padding: 0.75rem;
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .benefits-list {
    gap: 0.75rem;
  }

  .benefit-item {
    padding: 0.5rem;
    gap: 0.75rem;
  }

  .benefit-icon {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }

  .benefit-text {
    font-size: 0.9rem;
  }

  .modal-actions {
    gap: 0.5rem;
  }

  .install-btn {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
  }
}
</style>
